import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Returns a function that edits the query string of the current address (item 49).
 *
 * The edit starts from the live address, not from the last render. React Router applies a
 * location one render late, and `setSearchParams(prev => ...)` builds on that render's
 * copy, so two edits in the same tick would overwrite each other. The app uses
 * BrowserRouter (docs/c3/decisions/001-routing.md), so `window.location` is the live address.
 *
 * Edits replace the current history entry: a filter or a tab is not a place to go back to.
 */
export function useUpdateQuery(): (edit: (params: URLSearchParams) => void) => void {
  const navigate = useNavigate();
  return useCallback(
    (edit) => {
      const params = new URLSearchParams(window.location.search);
      edit(params);
      const search = params.toString();
      if (search === window.location.search.replace(/^\?/, '')) return;
      navigate(
        {
          pathname: window.location.pathname,
          search: search ? `?${search}` : '',
          hash: window.location.hash,
        },
        { replace: true }
      );
    },
    [navigate]
  );
}

/**
 * A list of values kept in one query parameter, comma-separated ("?severity=critical,high"), for a filter in which more
 * than one option can be chosen. One value, as the links from the pipeline write it, is a list of one; a value that is
 * not in `allowed` is left out, and an empty list leaves the parameter out of the address.
 */
export function useQueryList<T extends string>(key: string, allowed?: readonly T[]): [T[], (next: readonly T[]) => void] {
  const [params] = useSearchParams();
  const updateQuery = useUpdateQuery();
  const raw = params.get(key) ?? '';
  const list = useMemo(
    () =>
      raw
        .split(',')
        .map((part) => part.trim())
        .filter((part): part is T => part !== '' && (!allowed || (allowed as readonly string[]).includes(part))),
    [raw, allowed]
  );

  const setList = useCallback(
    (next: readonly T[]) =>
      updateQuery((out) => {
        if (next.length === 0) out.delete(key);
        else out.set(key, next.join(','));
      }),
    [key, updateQuery]
  );

  return [list, setList];
}

/**
 * One value of the console's state kept in the query string, so Back and shared links
 * reproduce it. A value equal to `fallback` is left out of the address, and a value not in
 * `allowed` reads as the fallback.
 */
export function useQueryParam<T extends string>(
  key: string,
  fallback: T,
  allowed?: readonly T[]
): [T, (next: T) => void] {
  const [params] = useSearchParams();
  const updateQuery = useUpdateQuery();
  const raw = params.get(key);
  const value = raw !== null && (!allowed || allowed.includes(raw as T)) ? (raw as T) : fallback;

  const setValue = useCallback(
    (next: T) =>
      updateQuery((out) => {
        if (next === fallback) out.delete(key);
        else out.set(key, next);
      }),
    [key, fallback, updateQuery]
  );

  return [value, setValue];
}
